import { Button, Modal, RadioGroup, Skeleton, useToast } from "@design";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DuplicatePair } from "@/api";
import { Text } from "@/components/Text";
import { VisibilityBadge } from "../VisibilityBadge";
import { useMergeErrorMessage, useMergePages, usePreviewMerge, useUndoMerge } from "./useDuplicates";

/**
 * Merge two pages: choose which one to keep, see exactly what will happen
 * (who will be able to see the merged page, which links get updated, the
 * merged markdown), then merge. The toast offers Undo; so does the history.
 */
export function MergeModal({
  pair,
  spaceUuid,
  onClose,
}: Readonly<{ pair: DuplicatePair; spaceUuid: string; onClose: () => void }>) {
  const { t } = useTranslation("knowledge");
  const toast = useToast();
  const message = useMergeErrorMessage();
  const [keep, setKeep] = useState(pair.a.path);
  const remove = keep === pair.a.path ? pair.b.path : pair.a.path;
  const preview = usePreviewMerge();
  const merge = useMergePages(spaceUuid);
  const undo = useUndoMerge(spaceUuid);
  const { mutate: runPreview } = preview;

  useEffect(() => {
    runPreview({ path: { space_uuid: spaceUuid }, body: { keepPath: keep, removePath: remove } });
  }, [runPreview, spaceUuid, keep, remove]);

  const plan = preview.data?.data;
  const submit = async () => {
    if (!plan) return;
    try {
      const res = await merge.mutateAsync({
        path: { space_uuid: spaceUuid },
        // The plan's own paths: exactly what was previewed (and its revisions).
        body: {
          keepPath: plan.keep.path,
          removePath: plan.remove.path,
          keepRevision: plan.keep.revision,
          removeRevision: plan.remove.revision,
        },
        throwOnError: true,
      });
      const record = res.data;
      toast.success(t("merge.merged", { removed: record.removedName, kept: record.keptName }), {
        action: {
          label: t("merge.undo"),
          onClick: () => undo.mutate({ path: { space_uuid: spaceUuid, merge_uuid: record.uuid } }),
        },
      });
      onClose();
    } catch (error) {
      toast.error(message(error, t("merge.failed")));
    }
  };

  let body: React.ReactNode;
  if (preview.isError) {
    body = (
      <Text role="alert" tone="error">
        {message(preview.error, t("merge.previewFailed"))}
      </Text>
    );
  } else if (!plan || preview.isPending) {
    body = <Skeleton className="h-48 w-full" />;
  } else {
    body = (
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Text as="span" variant="body-sm">
            {t("merge.visibleTo")}
          </Text>
          <VisibilityBadge visibility={plan.visibility} />
        </div>
        {plan.rewrittenPaths.length > 0 && (
          <Text variant="body-sm" tone="secondary">
            {t("merge.linksUpdated", { count: plan.rewrittenPaths.length })}
          </Text>
        )}
        {plan.linksFromOtherSpaces > 0 && (
          <Text variant="body-sm" tone="warning">
            {t("merge.otherSpaces", { count: plan.linksFromOtherSpaces })}
          </Text>
        )}
        <Text variant="caption" tone="tertiary">
          {t("merge.result", { title: plan.keep.title })}
        </Text>
        <Text
          as="pre"
          variant="code"
          className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md bg-surface-inset p-3"
        >
          {plan.content}
        </Text>
      </div>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t("merge.title")}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("merge.cancel")}
          </Button>
          <Button
            variant="primary"
            loading={merge.isPending}
            disabled={!plan || preview.isPending}
            onClick={() => void submit()}
          >
            {t("merge.confirm")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <RadioGroup
          aria-label={t("merge.keepLabel")}
          options={[pair.a, pair.b].map((page) => ({
            value: page.path,
            label: t("merge.keep", { title: page.title }),
            description: page.path,
          }))}
          value={keep}
          onChange={setKeep}
        />
        {body}
      </div>
    </Modal>
  );
}
